import { useId, useMemo } from "react";
import type { ReactNode } from "react";
import { BOARD, COLOR_GROUPS, PLAYER_COLORS } from "@grundbesitz/shared";
import type { PlayerState, PublicGameState, RoomMember } from "@grundbesitz/shared";
import { INNER, SIZE, jailLayout, placeTokens } from "../../lib/boardLayout";
import type { PlacedToken } from "../../lib/boardLayout";
import { BoardCenter } from "./BoardCenter";
import { FieldCell, hatchId, patternId } from "./FieldCell";
import type { OwnerMark } from "./FieldCell";
import { GroupPatternDef } from "./GroupBadge";
import { TokenIcon } from "./TokenIcon";
import { describeField } from "./fieldLabel";
import { useDiceDisplay, useTokenPositions } from "./useBoardMotion";
import type { ShownPosition } from "./useBoardMotion";
import "./board.css";

export interface BoardProps {
  game: PublicGameState;
  members: RoomMember[];
  /** The viewing player's id, null for spectators. */
  selfId: string | null;
  selectedField?: number | null;
  /** Called when a field is clicked or activated with the keyboard. */
  onFieldClick: (index: number) => void;
  /** Rendered in the middle of the board, next to the decks and the dice. */
  center?: ReactNode;
}

const HEX = new Map<string, string>(PLAYER_COLORS.map((c) => [c.id, c.hex]));

function initialOf(name: string): string {
  return (Array.from(name.trim())[0] ?? "?").toUpperCase();
}

function TokenMarker({
  placed,
  player,
  position,
  isTurn,
  isSelf,
}: {
  placed: PlacedToken;
  player: PlayerState;
  position: ShownPosition;
  isTurn: boolean;
  isSelf: boolean;
}) {
  const r = placed.size / 2;
  return (
    <g
      style={{
        transform: `translate(${placed.x}px, ${placed.y}px)`,
        transition: position.instant ? "none" : `transform ${position.stepMs}ms linear`,
      }}
    >
      {isTurn ? (
        <>
          <circle className="gb-turn-glow" r={r * 1.5} />
          <circle className="gb-turn-ring" r={r * 1.22} />
        </>
      ) : null}
      <circle className="gb-token-halo" r={r + 1} style={isSelf ? { stroke: "var(--accent)", strokeOpacity: 1, strokeWidth: 3 } : undefined} />
      <g transform={`translate(${-r} ${-r})`}>
        <TokenIcon token={player.token} color={player.color} size={placed.size} />
      </g>
    </g>
  );
}

function JailBars() {
  const { cell } = jailLayout(0, 0);
  const xs = [1, 2, 3, 4, 5].map((i) => cell.x + (cell.w / 6) * i);
  return (
    <g aria-hidden="true" pointerEvents="none">
      {xs.map((x) => (
        <line key={x} className="gb-jail-bar" x1={x} x2={x} y1={cell.y + 21} y2={cell.y + cell.h - 2} />
      ))}
      <rect x={cell.x} y={cell.y} width={cell.w} height={cell.h} rx="6" fill="none" stroke="var(--ink)" strokeWidth="3" />
    </g>
  );
}

/**
 * The play area: 40 fields in an SVG, tokens that walk along the path, and the middle
 * (decks, dice, free-parking pot and the `center` slot) as scaled HTML on top.
 */
export function Board({ game, members, selfId, selectedField, onFieldClick, center }: BoardProps) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const shown = useTokenPositions(game);
  const dice = useDiceDisplay(game);

  const marks = useMemo(() => {
    const map = new Map<string, OwnerMark>();
    for (const p of game.players) {
      map.set(p.id, { hex: HEX.get(p.color) ?? "#888888", initial: initialOf(p.name || members.find((m) => m.id === p.id)?.name || "?") });
    }
    return map;
  }, [game.players, members]);

  const labels = useMemo(() => BOARD.map((f) => describeField(f.index, game)), [game]);

  const alive = game.players.filter((p) => !p.eliminated);
  const placed = placeTokens(
    alive.map((p) => ({ id: p.id, field: shown[p.id]?.field ?? p.position, inJail: p.inJail })),
  );
  const turnId = game.phase === "GAME_OVER" ? null : game.turn.playerId;
  // The player on turn is drawn last so the marker is never covered.
  const ordered = [...alive].sort((a, b) => Number(a.id === turnId) - Number(b.id === turnId));
  const placedById = new Map(placed.map((p) => [p.id, p]));

  return (
    <div className="gb-board">
      <svg className="gb-svg" viewBox={`0 0 ${SIZE} ${SIZE}`} role="group" aria-label="Spielplan" focusable="false">
        <defs>
          {COLOR_GROUPS.map((g) => (
            <GroupPatternDef key={g} id={patternId(uid, g)} group={g} />
          ))}
          <pattern id={hatchId(uid)} width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="4" height="12" style={{ fill: "var(--ink)", fillOpacity: 0.3 }} />
          </pattern>
        </defs>
        <rect className="gb-frame" x="2" y="2" width={SIZE - 4} height={SIZE - 4} rx="20" />
        <rect className="gb-inner" x={INNER.x} y={INNER.y} width={INNER.w} height={INNER.h} />
        {BOARD.map((f) => {
          const state = game.properties[f.index];
          const ownerId = state?.owner ?? null;
          return (
            <FieldCell
              key={f.index}
              index={f.index}
              state={state}
              owner={ownerId ? (marks.get(ownerId) ?? null) : null}
              selected={selectedField === f.index}
              label={labels[f.index]}
              uid={uid}
              onClick={onFieldClick}
            />
          );
        })}
        <g className="gb-tokens">
          {ordered.map((p) => {
            const slot = placedById.get(p.id);
            if (!slot) return null;
            const position = shown[p.id] ?? { field: p.position, instant: true, stepMs: 140 };
            return <TokenMarker key={p.id} placed={slot} player={p} position={position} isTurn={p.id === turnId} isSelf={p.id === selfId} />;
          })}
        </g>
        <JailBars />
      </svg>
      <BoardCenter game={game} dice={dice} center={center} />
    </div>
  );
}

